import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { METHODS } from "../../../data/constants";
import { put } from "redux-saga/effects";
import { fetchAllPrinter, storeAllPrinters } from "../../slices/PrinterSlice";
import { saveOthersOffline } from "../../../helpers/OfflineHelper/shared";

/**
 * Uplifted from `redux-store/saga/printer` (apps/web 143 lines + apps/mobile 108).
 *
 * A real SPLIT: three of the six workers are identical and live here — including the
 * label-print-status WRITE-back, which was web-only until mobile gained label polling (2026-09-16).
 * The other three diverge, and `root` references them, so it belongs to the platform files. Both
 * watch SIX actions now. (Each variant names only its OWN symbols; this file is bundled on both
 * platforms, so a name here would show up in the other one's bundle — ledger, session 19.)
 *
 * ⚠️ Bare folder imports are REQUIRED in a `shared.ts` (hazard 16): `utils/apiService`,
 * `config/endpoints` and `slices/PrinterSlice` are all platform-split folders, and this file runs
 * on both platforms. `helpers/OfflineHelper/shared` is deep-imported to its LEAF instead —
 * `saveOthersOffline` is defined there, not in a variant, and routing through the leaf keeps core
 * acyclic (the rule session 19 established when the `OfflineUtil` chain landed).
 *
 * ⚠️ `resetSaleData` is NOT here and is not imported anywhere in this folder. Both apps imported
 * it from `helper/saleHelpers` and **neither ever called it** — a hazard-13 phantom, measured this
 * session across both files with strings and comments blanked. It is dropped under rule 2(b),
 * which is also why this saga did NOT have to wait for `saleHelpers`.
 */

/** Identical in both apps (quote style only). */
export function* savePrinterDetails(actions: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.PRINTER.savePrinter(),
      data: actions.payload,
    });
    yield put(fetchAllPrinter({}));
  } catch (error) {
    console.log(error);
  }
}

/**
 * Identical in both apps. Tags every fetched printer as `network` before storing, then mirrors the
 * list into the offline OTHERS store under the key `printer`.
 *
 * ⚠️ The `response.map` MUTATES each item in place (`item["printerType"] = "network"`) and returns
 * the same objects — both apps do; preserved.
 */
export function* fetchAllPrinterDetails(actions: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.PRINTER.fetchAllPrinter(),
    });
    if (response?.length) {
      var updatedResponse = response.map((item: any) => {
        item["printerType"] = "network";
        return item;
      });
      yield put(storeAllPrinters(updatedResponse));
      yield saveOthersOffline({
        type: "printer",
        items: updatedResponse,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * The item-label status WRITE-back — v1 `polling.js` `updateLabelStatus`, and the poller's label
 * claim. Web has always had it; mobile gained it with label polling (2026-09-16) and runs this same
 * worker, so it lives here rather than in both variants. Each `root` still watches for itself.
 */
export function* updateLabelPrintStatus(actions: any): Generator<any, void, any> {
  const { id, labelInfos, callback, errorCallback } = actions.payload;

  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.PRINTER.getLabelPrintStatusApi(id),
      data: { labelInfos },
      skipGlobalLoader: true,
      // `skipGlobalLoader` is the apps' own axios flag, read by apiService's interceptors; it is
      // not in `AxiosRequestConfig`, so the cast is required.
    } as any);

    if (!response?.code) {
      callback?.(response);
    } else {
      errorCallback?.(response);
    }
  } catch (error) {
    errorCallback?.(error);
    console.log(error);
  }
}
